import React, { useCallback, useState, ReactElement } from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Loader from '../../../components/loader/loader.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import Form from '../../../components/form/form.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormService from '../../../services/form-service';
import useScreenSmall from '../../../hooks/use-screen-small';

const DealScheduleModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    errorMessage,
    saveFormDeps,
    onCancel,
    onCreate,
    onCloseModal,
    onSubmitCreateForm,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);
  const formPrefix: string = `dealData.dealSchedulesData[${formKeyNew}]`;

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCreate) {
      onSubmitCreateForm((): void => {
        onCreate();
      });
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled, ...(saveFormDeps || [])]);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled) {
      setIsLeaveConfirmModalOpen(true);
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled]);

  return (
    <ModalLayout
      isOpen={isOpen}
      title="New Schedule"
      onCloseModal={cancelChangesAndCloseModal}
      onCloseModalByClickAway={closeModalByClickAway}
    >
      <Form>
        <Card isContainer withNoPadding>
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              <TableRow>
                <TableCell>
                  Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.name`} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
        <ModalButtonBlock>
          <Button onClick={cancelChangesAndCloseModal}>
            Cancel
          </Button>
          <Button isActive isDisabled={isSaving} onClick={applyChangesAndCloseModal}>
            Save
          </Button>
          {
            isSaving && !isSmallScreen && (
              <Loader isInline isModal isSmall />
            )
          }
        </ModalButtonBlock>
        {
          !!errorMessage && (
            <ErrorBlock errorMessage={errorMessage} />
          )
        }
      </Form>
      <LeaveConfirmModal
        isSubModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
    </ModalLayout>
  );
});

export default DealScheduleModal;
